import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import NLTWatermark from './NLTWatermark';
import { 
  Play, Pause, Heart, MessageCircle, Share2, MoreHorizontal,
  ThumbsDown, Flame, Guitar, Volume2, VolumeX, Plus, X,
  Loader2, Upload, Music, Eye, ChevronLeft, ChevronRight, Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Clips Feed Component
 * TikTok-style vertical scrolling video clips
 */
export const ClipsFeed = () => {
  const { token, user } = useAuth();
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    fetchClips();
  }, [token]);

  const fetchClips = async () => {
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/radio/clips/feed?limit=20`, { headers });
      const data = await res.json();
      setClips(data.clips || []);
    } catch (err) {
      console.error('Fetch clips error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  if (clips.length === 0) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white p-4">
        <Play className="w-16 h-16 text-gray-600 mb-4" />
        <h2 className="text-xl font-bold mb-2">No Clips Yet</h2>
        <p className="text-gray-400 text-center">Be the first to share a clip!</p>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="h-screen overflow-y-scroll snap-y snap-mandatory bg-black"
    >
      {clips.map((clip, index) => (
        <ClipCard 
          key={clip.id} 
          clip={clip} 
          isActive={index === currentIndex}
          onView={() => setCurrentIndex(index)}
        />
      ))}
    </div>
  );
};

/**
 * Single Clip Card Component
 */
export const ClipCard = ({ clip: initialClip, isActive, onView, hideBrand = false }) => {
  const { token, user } = useAuth();
  const videoRef = useRef(null);
  const [clip, setClip] = useState(initialClip);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [liked, setLiked] = useState(initialClip.user_liked || false);
  const [likeCount, setLikeCount] = useState(initialClip.likes?.length || 0);
  const [reaction, setReaction] = useState(initialClip.user_reaction);
  const [showReactions, setShowReactions] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const isProcessing = clip.status && clip.status !== 'ready';

  // Poll for transcode completion while the clip is still processing
  useEffect(() => {
    if (!isProcessing) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/radio/clips/${clip.id}`);
        const data = await res.json();
        if (data.clip && data.clip.status === 'ready') {
          setClip((prev) => ({ ...prev, ...data.clip }));
          setVideoError(false);
        }
      } catch (err) {
        console.error('Poll clip status error:', err);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [isProcessing, clip.id]);

  useEffect(() => {
    if (isActive && videoRef.current && !isProcessing) {
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
      // Track view
      fetch(`${API_URL}/api/radio/clips/${clip.id}/view`, { method: 'POST' });
    } else if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [isActive, clip.id, isProcessing]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleLike = async () => {
    if (!token) return;
    
    try {
      const method = liked ? 'DELETE' : 'POST';
      const res = await fetch(`${API_URL}/api/radio/clips/${clip.id}/like`, {
        method,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setLiked(!liked);
      setLikeCount(data.like_count);
    } catch (err) {
      console.error('Like error:', err);
    }
  };

  const handleReaction = async (reactionType) => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/radio/clips/${clip.id}/react?reaction=${reactionType}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setReaction(reactionType);
      setShowReactions(false);
    } catch (err) {
      console.error('Reaction error:', err);
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    // Share the backend OG page so crawlers (FB, X, WhatsApp, iMessage, LinkedIn)
    // render the clip's real thumbnail + video preview, then redirect humans to the app.
    const url = `${API_URL}/api/radio/og/clip/${clip.id}`;
    const shareData = {
      title: clip.title || 'NLT Radio Clip',
      text: `Check out ${clip.artist_name || clip.user_name || 'this artist'} on NLT Radio 🎵`,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
      }
    } catch (err) {
      // user cancelled or share failed — silently ignore
    }
  };

  return (
    <div 
      className="h-screen w-full snap-start relative bg-black flex items-center justify-center"
      onClick={togglePlay}
    >
      {/* Video */}
      <video
        ref={videoRef}
        src={clip.playback_url}
        poster={clip.thumbnail_url || undefined}
        className="h-full w-full object-contain"
        loop
        muted={isMuted}
        playsInline
        onEnded={() => setIsPlaying(false)}
        onError={() => { if (!isProcessing) setVideoError(true); }}
      />
      <NLTWatermark size="md" />

      {/* Processing Overlay */}
      {isProcessing && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70" data-testid="clip-processing-overlay">
          <Loader2 className="w-10 h-10 text-purple-400 animate-spin mb-3" />
          <p className="text-white font-medium">Processing video…</p>
          <p className="text-gray-400 text-sm mt-1">Optimizing for all devices</p>
        </div>
      )}

      {/* Playback Error Overlay */}
      {!isProcessing && videoError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 p-6 text-center" data-testid="clip-error-overlay">
          <X className="w-10 h-10 text-red-400 mb-3" />
          <p className="text-white font-medium">This video can't be played</p>
          <p className="text-gray-400 text-sm mt-1">The format may be unsupported on this device.</p>
        </div>
      )}

      {/* Play/Pause Indicator */}
      {!isProcessing && !videoError && !isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <Play className="w-20 h-20 text-white/80" fill="currentColor" />
        </div>
      )}

      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/60 to-transparent">
        <div className="flex items-center justify-between">
          <h2 className={`text-white font-bold text-lg ${hideBrand ? 'invisible' : ''}`}>Clips</h2>
          <button 
            onClick={(e) => { e.stopPropagation(); toggleMute(); }}
            className="p-2 bg-black/40 rounded-full"
          >
            {isMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
          </button>
        </div>
      </div>

      {/* Side Actions */}
      <div className="absolute right-3 bottom-32 flex flex-col items-center gap-5">
        {/* User Avatar */}
        <div className="relative">
          <div className="w-12 h-12 rounded-full bg-gray-700 overflow-hidden border-2 border-white">
            {clip.user_photo ? (
              <img src={clip.user_photo} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-lg font-bold">
                {clip.user_name?.charAt(0)}
              </div>
            )}
          </div>
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
            <Plus className="w-4 h-4 text-white" />
          </div>
        </div>

        {/* Like */}
        <button 
          onClick={(e) => { e.stopPropagation(); handleLike(); }}
          className="flex flex-col items-center"
        >
          <div className={`p-3 rounded-full ${liked ? 'bg-red-500' : 'bg-black/40'}`}>
            <Heart className={`w-7 h-7 ${liked ? 'text-white fill-current' : 'text-white'}`} />
          </div>
          <span className="text-white text-xs mt-1">{likeCount}</span>
        </button>

        {/* Reactions */}
        <div className="relative">
          <button 
            onClick={(e) => { e.stopPropagation(); setShowReactions(!showReactions); }}
            className="flex flex-col items-center"
          >
            <div className={`p-3 rounded-full ${reaction ? 'bg-orange-500' : 'bg-black/40'}`}>
              <Flame className="w-7 h-7 text-white" />
            </div>
            <span className="text-white text-xs mt-1">React</span>
          </button>

          {showReactions && (
            <div className="absolute right-14 top-0 flex gap-2 bg-black/80 rounded-full p-2">
              <button 
                onClick={(e) => { e.stopPropagation(); handleReaction('flop'); }}
                className={`p-2 rounded-full ${reaction === 'flop' ? 'bg-red-500' : 'bg-gray-700'}`}
              >
                <ThumbsDown className="w-5 h-5 text-white" />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); handleReaction('issa_hit'); }}
                className={`p-2 rounded-full ${reaction === 'issa_hit' ? 'bg-orange-500' : 'bg-gray-700'}`}
              >
                <Flame className="w-5 h-5 text-white" />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); handleReaction('rocking_with_it'); }}
                className={`p-2 rounded-full ${reaction === 'rocking_with_it' ? 'bg-blue-500' : 'bg-gray-700'}`}
              >
                <Guitar className="w-5 h-5 text-white" />
              </button>
            </div>
          )}
        </div>

        {/* Views */}
        <div className="flex flex-col items-center">
          <div className="p-3 rounded-full bg-black/40">
            <Eye className="w-7 h-7 text-white" />
          </div>
          <span className="text-white text-xs mt-1">{clip.views || 0}</span>
        </div>

        {/* Share */}
        <button className="flex flex-col items-center" onClick={handleShare} data-testid="clip-share-btn">
          <div className="p-3 rounded-full bg-black/40">
            <Share2 className="w-7 h-7 text-white" />
          </div>
          <span className="text-white text-xs mt-1">Share</span>
        </button>
      </div>

      {/* Bottom Info */}
      <div className="absolute bottom-0 left-0 right-16 p-4 bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-bold text-white">@{clip.artist_name || clip.user_name}</span>
          {clip.is_artist && (
            <span className="px-2 py-0.5 bg-purple-500/50 rounded text-xs text-white">Artist</span>
          )}
        </div>
        <p className="text-white text-sm mb-2">{clip.title}</p>
        {clip.description && (
          <p className="text-gray-300 text-sm mb-2 line-clamp-2">{clip.description}</p>
        )}
        
        {/* Attached Track */}
        {clip.track && (
          <div className="flex items-center gap-2 bg-black/40 rounded-full px-3 py-1.5 w-fit">
            <Music className="w-4 h-4 text-white animate-spin" style={{ animationDuration: '3s' }} />
            <span className="text-white text-sm truncate max-w-[200px]">
              {clip.track.title} - {clip.track.artist_name}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Clip Upload Component
 */
export const ClipUpload = ({ onClose, onSuccess }) => {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [showTrackPicker, setShowTrackPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Fetch tracks for attachment
    const fetchTracks = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/artists/radio/tracks?limit=50`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const data = await res.json();
        setTracks(data.tracks || []);
      } catch (err) {
        console.error('Fetch tracks error:', err);
      }
    };
    fetchTracks();
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    // Validate video
    if (!selectedFile.type.startsWith('video')) {
      alert('Clips must be videos');
      return;
    }
    
    // Max 2GB
    if (selectedFile.size > 2 * 1024 * 1024 * 1024) {
      alert('Clip must be under 2GB');
      return;
    }
    
    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
  };

  const handleUpload = async () => {
    if (!file || !token) return;
    
    setUploading(true);
    setProgress(0);
    
    try {
      const contentType = file.type || 'video/mp4';

      // Generate a poster thumbnail in the browser and upload it to R2 first, so
      // the clip is immediately "ready" (has a thumbnail + plays) without waiting
      // on the server transcode (which can fail/stall in production).
      let thumbnailKey = null;
      try {
        const thumbBlob = await new Promise((resolve) => {
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.muted = true;
          video.playsInline = true;
          const url = URL.createObjectURL(file);
          video.src = url;
          let settled = false;
          const finish = (blob) => { if (!settled) { settled = true; URL.revokeObjectURL(url); resolve(blob); } };
          video.onloadedmetadata = () => {
            const t = Math.min(1, (video.duration || 2) / 2);
            try { video.currentTime = t; } catch (e) { finish(null); }
          };
          video.onseeked = () => {
            try {
              const w = video.videoWidth, h = video.videoHeight;
              if (!w || !h) return finish(null);
              const maxDim = 720;
              const scale = Math.max(w, h) > maxDim ? maxDim / Math.max(w, h) : 1;
              const canvas = document.createElement('canvas');
              canvas.width = Math.round(w * scale);
              canvas.height = Math.round(h * scale);
              canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
              canvas.toBlob((b) => finish(b), 'image/jpeg', 0.8);
            } catch (e) { finish(null); }
          };
          video.onerror = () => finish(null);
          setTimeout(() => finish(null), 8000);
        });
        if (thumbBlob) {
          const tRes = await fetch(
            `${API_URL}/api/radio/clips/presigned-upload?filename=thumb.jpg&content_type=${encodeURIComponent('image/jpeg')}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (tRes.ok) {
            const tData = await tRes.json();
            const putT = await fetch(tData.upload_url, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: thumbBlob });
            if (putT.ok) thumbnailKey = tData.r2_key;
          }
        }
      } catch (e) { /* thumbnail is best-effort */ }

      // 1. Get presigned direct-to-R2 upload URL
      const presignRes = await fetch(
        `${API_URL}/api/radio/clips/presigned-upload?filename=${encodeURIComponent(file.name)}&content_type=${encodeURIComponent(contentType)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!presignRes.ok) throw new Error('Could not start upload');
      const { upload_url, clip_id, r2_key } = await presignRes.json();

      // 2. Upload the file directly to R2 with progress
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', upload_url);
        xhr.setRequestHeader('Content-Type', contentType);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Upload failed')));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(file);
      });

      // 3. Confirm upload -> creates clip + triggers transcode
      const form = new FormData();
      form.append('clip_id', clip_id);
      form.append('r2_key', r2_key);
      form.append('content_type', contentType);
      if (title) form.append('title', title);
      if (description) form.append('description', description);
      if (selectedTrack) form.append('track_id', selectedTrack.id);
      if (thumbnailKey) form.append('thumbnail_key', thumbnailKey);

      const confirmRes = await fetch(`${API_URL}/api/radio/clips/confirm-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: form
      });
      const data = await confirmRes.json();

      if (confirmRes.ok && data.success) {
        onSuccess && onSuccess(data.clip);
        onClose();
      } else {
        alert(data.detail || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-800">
        <button onClick={onClose} className="p-2">
          <X className="w-6 h-6 text-white" />
        </button>
        <h2 className="font-semibold text-white">Upload Clip</h2>
        <button 
          onClick={handleUpload}
          disabled={!file || uploading}
          className="px-4 py-2 bg-purple-600 rounded-full text-sm font-medium text-white disabled:opacity-50"
          data-testid="post-clip-btn"
        >
          {uploading ? (progress > 0 && progress < 100 ? `${progress}%` : <Loader2 className="w-4 h-4 animate-spin" />) : 'Post'}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {!file ? (
          <div 
            onClick={() => fileInputRef.current?.click()}
            className="h-64 border-2 border-dashed border-gray-600 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-purple-500 transition"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Upload className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-gray-400">Tap to select a video clip</p>
            <p className="text-gray-500 text-sm mt-2">Max 60 seconds</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Preview */}
            <div className="relative rounded-xl overflow-hidden bg-gray-900">
              <video 
                src={preview}
                className="w-full max-h-80 object-contain"
                controls
              />
              <button 
                onClick={() => { setFile(null); setPreview(null); }}
                className="absolute top-2 right-2 p-2 bg-black/60 rounded-full"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Title */}
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Add a title..."
              className="w-full bg-gray-800 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
              maxLength={100}
            />

            {/* Description */}
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description..."
              className="w-full bg-gray-800 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              rows={3}
              maxLength={300}
            />

            {/* Track Attachment */}
            <div>
              <button 
                onClick={() => setShowTrackPicker(!showTrackPicker)}
                className="flex items-center gap-2 text-purple-400"
              >
                <Music className="w-5 h-5" />
                {selectedTrack ? selectedTrack.track_title : 'Add a song'}
              </button>
              
              {showTrackPicker && (
                <div className="mt-2 max-h-40 overflow-y-auto bg-gray-800 rounded-xl">
                  {tracks.map(track => (
                    <button
                      key={track.id}
                      onClick={() => { setSelectedTrack(track); setShowTrackPicker(false); }}
                      className={`w-full flex items-center gap-3 p-3 text-left hover:bg-gray-700 ${
                        selectedTrack?.id === track.id ? 'bg-purple-900/50' : ''
                      }`}
                    >
                      <Music className="w-4 h-4 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{track.track_title}</p>
                        <p className="text-gray-400 text-xs truncate">{track.artist_name}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * User's Clips Gallery Component
 */
export const UserClipsGallery = ({ userId, canDelete = false }) => {
  const { token } = useAuth();
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClip, setSelectedClip] = useState(null);

  useEffect(() => {
    if (!userId) return;
    
    const fetchClips = async () => {
      try {
        const res = await fetch(`${API_URL}/api/radio/clips/user/${userId}`);
        const data = await res.json();
        setClips(data.clips || []);
      } catch (err) {
        console.error('Fetch clips error:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchClips();
  }, [userId]);

  const handleDelete = async (clipId) => {
    if (!token) {
      alert('Please log in to delete clips');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this clip? This cannot be undone.')) {
      return;
    }
    
    try {
      console.log('Deleting clip:', clipId);
      
      const res = await fetch(`${API_URL}/api/radio/clips/${clipId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await res.json().catch(() => ({}));
      console.log('Delete clip response:', res.status, data);
      
      if (res.ok) {
        setClips(prev => prev.filter(c => c.id !== clipId));
        alert('Clip deleted successfully!');
      } else {
        alert(data.detail || data.message || 'Failed to delete clip. Please try again.');
      }
    } catch (err) {
      console.error('Delete clip error:', err);
      alert('Error deleting clip: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
      </div>
    );
  }

  if (clips.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Play className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p>No clips yet</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-1">
      {clips.map(clip => (
        <div 
          key={clip.id}
          className="relative aspect-[9/16] bg-gray-800 rounded-lg overflow-hidden cursor-pointer group"
          onClick={() => setSelectedClip(clip)}
        >
          <video 
            src={clip.playback_url}
            poster={clip.thumbnail_url || undefined}
            className="w-full h-full object-cover"
            preload="metadata"
          />
          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition flex items-center justify-center">
            {clip.status && clip.status !== 'ready' ? (
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            ) : (
              <Play className="w-8 h-8 text-white opacity-80" fill="currentColor" />
            )}
          </div>
          <div className="absolute bottom-1 left-1 flex items-center gap-1 text-white text-xs">
            <Eye className="w-3 h-3" />
            {clip.views || 0}
          </div>
          {/* Delete Button - Always visible on mobile, hover on desktop */}
          {canDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(clip.id); }}
              className="absolute top-1 right-1 p-2 bg-red-500 hover:bg-red-600 rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition z-10 shadow-lg"
              data-testid={`delete-clip-${clip.id}`}
            >
              <Trash2 className="w-3 h-3 text-white" />
            </button>
          )}
        </div>
      ))}

      {/* Clip Viewer Modal */}
      {selectedClip && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          {/* Close Button */}
          <button 
            onClick={() => setSelectedClip(null)}
            className="absolute top-4 right-4 p-2 z-10 bg-white/10 hover:bg-white/20 rounded-full transition"
            data-testid="close-clip-viewer"
          >
            <X className="w-8 h-8 text-white" />
          </button>
          {/* Delete Button in Modal */}
          {canDelete && (
            <button
              className="absolute top-4 left-4 p-3 bg-red-500 hover:bg-red-600 rounded-full transition flex items-center gap-2 z-10"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(selectedClip.id);
                setSelectedClip(null);
              }}
              data-testid="delete-clip-modal"
            >
              <Trash2 className="w-5 h-5 text-white" />
              <span className="text-white text-sm font-medium hidden sm:inline">Delete</span>
            </button>
          )}
          <ClipCard clip={selectedClip} isActive={true} />
        </div>
      )}
    </div>
  );
};

// Re-export for default imports
export default ClipsFeed;
